import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex } from 'Src/components/layout/Flex';
import { AuthRequestUIBlocker } from 'Src/feature/auth-request/AuthRequestUIBlocker';
import { useCurrentAuthRequest } from 'Src/feature/auth-request/useCurrentAuthRequest';
import { ListModal } from 'Src/feature/modal/ListModal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { TextModal } from 'Src/feature/modal/TextModal';
import { Page } from 'Src/feature/page/Page';
import { PageMain } from 'Src/feature/page/PageMain';
import { PageMainHeader } from 'Src/feature/page/PageMainHeader';
import { TerminalDetails } from 'Src/feature/pos/TerminalDetails';
import { Field, Form, usePOSLogin } from 'Src/feature/pos/usePOSLogin';
import { CardReaderStatus } from 'Src/feature/status/CardReaderStatus';
import { ConnectionStatus } from 'Src/feature/status/ConnectionStatus';
import { BootDataPrimerTerminal } from 'Src/network/BootDataPrimerTerminal';

/**
 * Component for the main pin pad login for the POS
 */
const POSLogin: FC = () => {
  const { t } = useTranslation();
  const store = usePOSLogin();
  const currentAuthRequest = useCurrentAuthRequest();

  return (
    <>
      <BootDataPrimerTerminal />
      <AuthRequestUIBlocker key={currentAuthRequest?.currentRequest?.id} />
      <Page
        main={(
          <PageMain
            header={<PageMainHeader end={<><CardReaderStatus onClick={store.reInitCardReader} /><ConnectionStatus /></>} />}
            main={(
              <Flex padding="xxl" gap="none" horizontal justifyContent="space-around" alignItems="stretch">
                <TerminalDetails terminal={store.terminalLocation.data?.terminal} location={store.terminalLocation.data?.location} />
                <Form usedForm={store.form} omitChangeTracking>
                  <Field
                    type="pin"
                    name="pin"
                    size="xl"
                    isLoading={store.isLoading}
                    onSend={() => {
                      void store.logIn();
                    }}
                    onAfterChange={() => {
                      store.form.action.setValue('swipe', undefined, { shouldValidate: true, shouldTouch: true, shouldDirty: true });
                    }}
                  />
                </Form>
              </Flex>
            )}
          />
        )}
      />
      <TextModal
        open={store.showErrorModal}
        title={t('posLogin.invalidLoginDialog.title')}
        color="error"
        textContent={store.serverErrorMsg || t('posLogin.invalidLoginDialog.defaultMsg')}
        actions={(
          <ModalAction
            color="secondary"
            label={t('labels.okay')}
            onClick={store.onErrorModalOk}
          />
        )}
      />
      <ListModal
        title="Select a Job Position"
        data-testid="job-position-modal"
        open={!!store.availableJobPositions && store.availableJobPositions?.length !== 0}
        options={store.jobPositionOptions || []}
        onOptionClick={(jobPositionOption) => {
          void store.logIn(jobPositionOption.value);
        }}
        onAfterOptionClick={() => {
          // do nothing after option click as modal will navigate to a new route
        }}
      />
      <TextModal
        data-testid="end-break-modal"
        open={store.showEndBreakModal}
        title={t('posLogin.endBreakDialog.titleEndBreak')}
        textContent={t('posLogin.endBreakDialog.bodyEndBreak')}
        onBackDropClick={store.onDoNotEndBreakModal}
        actions={(
          <>
            <ModalAction
              onClick={store.onDoNotEndBreakModal}
              label={t('posLogin.endBreakDialog.actionStayOnBreak')}
            />
            <ModalAction
              color="primary"
              onClick={store.onEndBreakModal}
              label={t('posLogin.endBreakDialog.actionEndBreak')}
            />
          </>
        )}
      />
    </>
  );
};

export default POSLogin;
